import { useState, useEffect } from 'react';
import { AIChatPanel } from '@myspend/agent-kit/ui';
import { T } from '../theme';
import { LogoMark } from '../theme';
import { useWindowWidth } from '../hooks/useWindowWidth';
import {
  askBookkeeper, condenseBkHistory,
  loadLLMCatalogue, loadBookkeeperModelPref, saveBookkeeperModelPref,
  subscribeToMemory,
} from '../utils/bookkeeperApi';
import { subscribeToPendingFacts } from '../utils/advisorStorage';
import AISettingsModal from './AISettingsModal';
import { useAuth } from '../contexts/AuthContext';

const SUGGESTIONS = [
  'Hi, tell me about how you can help',
  'What are my total business expenses?',
  'How many transactions need review?',
  'What were my top expenses last month?',
  'Create a pro-forma Schedule C for tax purposes for my business',
];

const SAMPLES_TEXT = `You can use me to drill down further into your data by asking specific questions. Here are a few ways to get more detailed information:

### 1. Dig into Specific Categories
If you want to know exactly what made up a category, just ask:
*   *"What transactions make up the $2,100 in 'Food & Dining' expenses?"*
*   *"Show me all the items included in 'Travel'."*
*   *"Which specific companies/merchants are under 'Other Expenses'?"*

### 2. Search by Date or Account
You can narrow the focus to a specific month, quarter, or account:
*   *"What was my total spending on my 'Chase4426' account in April?"*
*   *"List all business expenses from May 2026."*
*   *"Show me all transactions from my 'Amex' account categorized as 'Technology'."*

### 3. Review Unclassified Items
You can use me to help clean up your records so your tax summary is more accurate:
*   *"Show me the unclassified transactions for this month."*
*   *"Can you help me categorize the 2 pending transactions?"*

### 4. Expense Report Auditing
If you want to verify what's in your reports versus what's in your bank:
*   *"Which expense reports include 'United Airlines'?"*
*   *"Are there any expenses in my 'Detroit tech 2026' report that aren't linked to a bank transaction?"*

### 5. Ask for "What-If" Analysis
*   *"If I only include 50% of my 'Food & Dining' as a business expense, how much does that change my total deductions?"*
*   *"How much have I spent on 'Office Business' compared to 'Technology' this year?"*

### 6. Charts and Graphs
Ask for a visualisation any time — I'll pick the best chart type automatically:
*   *"Chart my spending by category this month."*
*   *"Show a bar chart of my top 8 expense categories year to date."*
*   *"Plot my monthly expenses as a line chart for 2026."*
*   *"Pie chart of my business vs personal vs income transactions."*
*   *"Show a horizontal bar chart comparing my accounts by total spend."*

**Pro Tip:** If you ever aren't sure what to ask, you can simply say:
> *"What are my biggest categories for 2026 so far?"*

...and I will pull the latest summary for you to help you decide where to investigate further. What would you like to explore next?`;

const HELP_TEXT = `**Available commands**

| Command | Description |
|---|---|
| \`/clear\` | Clear the conversation and reset turns |
| \`/condense\` | Summarise older context, keep last 3 turns |
| \`/show_summary\` | Show the last condensed summary and token counts |
| \`/help\` | Show this message |
| \`/usage\` | Show token and query usage statistics |
| \`/samples\` | Show sample questions you can ask |
| \`/model\` | Choose the AI model for your session |
| \`/model <model-id>\` | Select a specific model by name |

**I can help you with**
- Business income and expense totals
- Transactions needing review
- Financial summaries by period or category
- Expense report details
- Tax deduction breakdowns

Ask in plain English — I'll look up your data to answer.`;

const BK_THEME = { ...T };

export default function BookkeeperPanel({ onClose, messages, usage, limits, onNewMessage, onReply, onClear, onCondense }) {
  const { user, account } = useAuth();
  const windowWidth = useWindowWidth();
  const [showAISettings, setShowAISettings] = useState(false);
  const [aiSettingsTab, setAISettingsTab]   = useState('memory');
  const [memoryCount, setMemoryCount]       = useState(0);
  const [pendingCount, setPendingCount]     = useState(0);

  useEffect(() => {
    if (!user) return;
    const u1 = subscribeToMemory(user.uid, facts => setMemoryCount(facts.length));
    const u2 = subscribeToPendingFacts(user.uid, pf => setPendingCount(pf.length));
    return () => { u1(); u2(); };
  }, [user]);

  return (
    <>
      <AIChatPanel
        onClose={onClose}
        messages={messages}
        usage={usage}
        limits={limits}
        onNewMessage={onNewMessage}
        onReply={onReply}
        onClear={onClear}
        onCondense={onCondense}
        onAsk={(msg, history, prevTokensIn) => askBookkeeper(msg, history, prevTokensIn)}
        onCondenseHistory={(history) => condenseBkHistory(history)}
        displayName="MySpend AI"
        subtitle="Ask about your expenses & transactions"
        icon={<LogoMark size={26} />}
        suggestions={SUGGESTIONS}
        helpText={HELP_TEXT}
        samplesText={SAMPLES_TEXT}
        loadCatalogue={loadLLMCatalogue}
        loadModelPref={loadBookkeeperModelPref}
        saveModelPref={saveBookkeeperModelPref}
        userUid={user?.uid}
        userTier={account?.tier ?? 'basic'}
        memoryCount={memoryCount}
        pendingCount={pendingCount}
        onOpenSettings={(tab) => { setAISettingsTab(tab ?? 'memory'); setShowAISettings(true); }}
        theme={BK_THEME}
        windowWidth={windowWidth}
      />
      {showAISettings && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000 }}>
          <AISettingsModal
            uid={user?.uid}
            tier={account?.tier ?? 'basic'}
            initialTab={aiSettingsTab}
            onClose={() => setShowAISettings(false)}
          />
        </div>
      )}
    </>
  );
}
